// İzin (leave) ortak yardımcıları — etiketler, gün hesabı ve bakiye.
// ProfilePage (talep formu), LeaveRequestsPage (yönetim + rapor) ve
// EmployeeLeaveModal tarafından paylaşılan tek doğruluk kaynağıdır.

import { getHolidayMapForRange, isReasonableRange, toDateKey } from './turkishHolidays';

// İzin türü kodlarının Türkçe karşılıkları (statik).
export const LEAVE_TYPE_LABELS = {
  yillik: 'Yıllık İzin',
  mazeret: 'Mazeret İzni',
  raporlu: 'Raporlu',
  dogum_oncesi: 'Doğum Öncesi İzin',
  babalik: 'Babalık İzni',
  evlilik: 'Evlilik İzni',
  olum: 'Ölüm İzni',
  engelli_cocuk: 'Engelli Çocuk Mazeret İzni',
  dogum_sonrasi: 'Doğum Sonrası İzin',
  yatakli_tedavi: 'Yataklı Kurumda Tedavi',
  refakat: 'Refakat İzni',
  sua: 'Şua İzni',
  saglik_kurulu: 'Sağlık Kurulu Raporu',
  ayakta_tedavi: 'Ayakta Tedavi',
};

export const translateLeaveType = (type) => LEAVE_TYPE_LABELS[type] || type;

// Bakiyeden düşen / kotaya tabi izin türü.
export const ANNUAL_LEAVE_KEY = 'yillik';

// Yeni çalışan için varsayılan yıllık izin (gün).
export const DEFAULT_ANNUAL_PERMIT = 14;

// Başlangıç ve bitiş tarihine göre izin gün sayısını hesaplar.
// Cumartesi = 0.5 gün, Pazar = 0 gün, hafta içi = 1 gün.
// Resmi tatiller (Türkiye) izin süresinden DÜŞÜLMEZ:
//   tam gün tatil → 0 gün, yarım gün (arefe) → en fazla 0,5 gün sayılır.
export const calculateLeaveDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  // Geçersiz / yazılmakta olan yıl (0002 vb.) veya aşırı uzun aralık → hesaplama yapma
  if (!isReasonableRange(start, end)) return 0;

  const holidayMap = getHolidayMapForRange(start, end);

  let totalDays = 0;
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    let dayValue;
    if (dayOfWeek === 0) {
      dayValue = 0; // Pazar
    } else if (dayOfWeek === 6) {
      dayValue = 0.5; // Cumartesi
    } else {
      dayValue = 1; // Hafta içi
    }

    const holiday = holidayMap.get(toDateKey(current));
    if (holiday) {
      // Tam gün tatil hiç sayılmaz; arefe (yarım gün) en fazla 0,5 sayılır.
      dayValue = holiday.type === 'full' ? 0 : Math.min(dayValue, 0.5);
    }

    totalDays += dayValue;
    current.setDate(current.getDate() + 1);
  }
  return totalDays;
};

// Çalışanın yıllık izin hakkı (gün). Tanımsızsa varsayılan 14.
export const getAnnualEntitlement = (employee) =>
  Number(employee?.annualPermit) || DEFAULT_ANNUAL_PERMIT;

// Tarih alanından yıl çıkarır (string veya Firestore Timestamp).
const yearOf = (dateValue) => {
  if (!dateValue) return null;
  try {
    const d = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    return Number.isNaN(d.getTime()) ? null : d.getFullYear();
  } catch {
    return null;
  }
};

// Yıllık izin devrinin başladığı yıl. İzin kayıtları sisteme 2025'te girilmeye
// başlandı; önceki yılların kaydı olmadığından devre katılmazlar (katılsalar
// "hiç kullanılmamış" sayılıp kalanı şişirirdi).
export const LEAVE_CARRY_START_YEAR = 2025;

// Çalışanın bakiyesinin birikmeye başladığı yıl: işe giriş yılı (Employees.startDate),
// en erken LEAVE_CARRY_START_YEAR.
export const getCarryStartYear = (employee) =>
  Math.max(LEAVE_CARRY_START_YEAR, yearOf(employee?.startDate) || LEAVE_CARRY_START_YEAR);

// Bir kaydın yıllık izin olup olmadığını belirler.
// LeaveRequests'te alan `leaveType`, EmployeeLeaves'te `typeKey` olabilir.
const isAnnualLeave = (record) =>
  (record.leaveType || record.typeKey) === ANNUAL_LEAVE_KEY;

/**
 * Belirli bir yıl için yıllık izin bakiyesini hesaplar. Önceki yıllardan
 * kalan (veya avans kullanılıp eksiye düşen) günler o yıla devreder.
 * @returns {{ entitlement:number, annual:number, carryOver:number, used:number, reserved:number, remaining:number }}
 *
 * annual    = o yılın hakkı (Employees.annualPermit)
 * carryOver = carryFromYear'dan önceki yıla kadar her yılın kalanı (eksi de devreder)
 * entitlement = annual + carryOver (toplam hak)
 * used     = o yıl ONAYLANMIŞ yıllık izin günleri (LeaveRequests + EmployeeLeaves)
 * reserved = o yıl BEKLEYEN (PENDING) yıllık izin talepleri (LeaveRequests)
 * remaining = entitlement - used - reserved (birikmiş toplam kalan)
 *
 * @param entitlement      Çalışanın yıllık izin hakkı (gün)
 * @param leaveRequests    Çalışana ait LeaveRequests kayıtları
 * @param employeeLeaves   Çalışana ait EmployeeLeaves kayıtları (admin manuel)
 * @param year             Hesaplanacak takvim yılı (varsayılan: bu yıl)
 * @param carryFromYear    Devrin başladığı yıl (getCarryStartYear); verilmezse devir yok
 * @param excludeRequestId Hesaptan çıkarılacak talep id'si (taslak/düzenleme)
 */
export const computeAnnualBalance = ({
  entitlement,
  leaveRequests = [],
  employeeLeaves = [],
  year = new Date().getFullYear(),
  carryFromYear = null,
  excludeRequestId = null,
}) => {
  const annual = Number(entitlement) || 0;

  const sumDays = (records, statuses, inYear) =>
    records
      .filter((r) => {
        if (excludeRequestId && r.id === excludeRequestId) return false;
        if (!isAnnualLeave(r)) return false;
        if (yearOf(r.startDate) !== inYear) return false;
        // EmployeeLeaves eski kayıtlarda status olmayabilir → onaylanmış say.
        const status = r.status || 'APPROVED';
        return statuses.includes(status);
      })
      .reduce((acc, r) => acc + (Number(r.leaveDays) || 0), 0);

  const all = [...leaveRequests, ...employeeLeaves];

  let carryOver = 0;
  for (let y = carryFromYear || year; y < year; y += 1) {
    carryOver += annual - sumDays(all, ['APPROVED', 'PENDING'], y);
  }

  const used = sumDays(all, ['APPROVED'], year);
  const reserved = sumDays(all, ['PENDING'], year);
  const total = annual + carryOver;
  const remaining = total - used - reserved;

  return { entitlement: total, annual, carryOver, used, reserved, remaining };
};
